import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import type { Workspace, BlameLine } from '../engine/types'
import { computeLineMapping } from '../engine/diff'

// --- Local blame line (client-side overlay) ---
interface LocalBlameLine extends BlameLine {
    local: true
}

export interface BlameResult {
    loading: boolean
    stale: boolean
    lines: BlameLine[]
}

/**
 * useBlame – dual-layer live blame hook.
 *
 * Layer 1: Committed blame from engine (re-fetched on path change, save, external sync)
 * Layer 2: Local dirty overlay (client-side line diff vs committed content)
 *
 * Merged output: lines where locally-edited lines show { local: true, writerName: 'You' }
 */
export function useBlame(
    workspace: Workspace | null,
    path: string | null,
    content: string | null,
    saveVersion = 0,
): BlameResult {
    // Committed blame from engine
    const [committed, setCommitted] = useState<{ loading: boolean; lines: BlameLine[] }>({
        loading: false,
        lines: [],
    })
    // The content at the time committed blame was fetched
    const [committedContent, setCommittedContent] = useState<string | null>(null)
    const reqRef = useRef(0)
    // Keep content in a ref so the fetch callback can snapshot current content
    const contentRef = useRef(content)
    contentRef.current = content

    // Shared fetch-blame logic — used by saveVersion dep, external-change, and fs:changed
    const fetchBlame = useCallback(() => {
        if (!workspace || !path || !workspace.history?.blame) return
        const reqId = ++reqRef.current
        setCommitted(prev => ({ loading: true, lines: prev.lines }))
        workspace.history.blame(path).then(blameLines => {
            if (reqId !== reqRef.current) return
            setCommittedContent(contentRef.current)
            setCommitted({ loading: false, lines: blameLines })
        }).catch(() => {
            if (reqId !== reqRef.current) return
            setCommitted(prev => ({ loading: false, lines: prev.lines }))
        })
    }, [workspace, path])

    // Fetch blame from engine — triggered by path, saveVersion, or workspace changes
    useEffect(() => {
        if (!workspace || !path || !workspace.history?.blame) {
            setCommitted({ loading: false, lines: [] })
            setCommittedContent(null)
            return
        }
        // Initial fetch + re-fetch on save
        const reqId = ++reqRef.current
        setCommitted(prev =>
            prev.lines.length > 0
                ? { loading: true, lines: prev.lines }
                : { loading: true, lines: [] },
        )
        workspace.history.blame(path).then(blameLines => {
            if (reqId !== reqRef.current) return
            setCommittedContent(contentRef.current)
            setCommitted({ loading: false, lines: blameLines })
        }).catch(() => {
            if (reqId !== reqRef.current) return
            setCommitted(prev => ({ loading: false, lines: prev.lines }))
        })
    }, [workspace, path, saveVersion])

    // Listen for external changes (sync/merge) → re-fetch blame immediately
    useEffect(() => {
        if (!workspace || !path || !workspace.history?.blame) return
        const handler = (ev: { paths?: string[] }) => {
            if (ev.paths?.includes(path)) fetchBlame()
        }
        workspace.on('fs:external-change', handler)
        return () => workspace.off('fs:external-change', handler)
    }, [workspace, path, fetchBlame])

    // Listen for local writes (agent, sync-merge) → re-fetch blame (debounced)
    // Debounce avoids double-fetching when editor auto-save triggers both
    // saveVersion (via dependency array above) and fs:changed in quick succession.
    useEffect(() => {
        if (!workspace || !path || !workspace.history?.blame) return
        let timer: ReturnType<typeof setTimeout> | null = null
        const handler = (ev: { paths?: string[] }) => {
            if (!ev.paths?.includes(path)) return
            if (timer) clearTimeout(timer)
            timer = setTimeout(fetchBlame, 300)
        }
        workspace.on('fs:changed', handler)
        return () => {
            workspace.off('fs:changed', handler)
            if (timer) clearTimeout(timer)
        }
    }, [workspace, path, fetchBlame])

    // Merge committed blame with local dirty overlay
    const mergedLines = useMemo((): BlameLine[] => {
        const cLines = committed.lines
        if (!cLines.length || content == null) return cLines
        if (committedContent == null) return cLines

        // Fast path: content unchanged since blame fetched
        if (content === committedContent) return cLines

        const currentSplit = content.split('\n')
        const committedSplit = committedContent.split('\n')

        // Use LCS-based diff to map old lines → new positions.
        // This correctly handles insertions/deletions without
        // marking merely-shifted lines as locally modified.
        const mapping = computeLineMapping(committedSplit, currentSplit)

        const merged: BlameLine[] = mapping.map(m => {
            if (m.oldIndex != null && m.oldIndex < cLines.length) {
                // Line content unchanged — preserve committed blame
                return { ...cLines[m.oldIndex], lineNumber: m.newIndex + 1 }
            }
            // Line is new or modified locally
            const localLine: LocalBlameLine = {
                lineNumber: m.newIndex + 1,
                text: currentSplit[m.newIndex],
                commitHash: null,
                writerKid: null,
                writerName: 'You',
                timestamp: null,
                local: true,
            }
            return localLine
        })
        return merged
    }, [committed.lines, content, committedContent])

    // Derive stale: has local edits that haven't been committed yet
    const hasLocalEdits = content != null && committedContent != null && content !== committedContent

    return {
        loading: committed.loading,
        stale: hasLocalEdits && !committed.loading,
        lines: mergedLines,
    }
}
